import { useEffect, useRef } from "react";

import { useEventCallback } from "usehooks-ts";

import { useMutationObserver } from "./useMutationObserver";

import type { RefObject } from "react";

const KEYS = {
  horizontal: { next: "ArrowRight", previous: "ArrowLeft" },
  vertical: { next: "ArrowDown", previous: "ArrowUp" },
};

function isTextField(element: HTMLElement) {
  return element.isContentEditable || !!element.closest("input, textarea");
}

/**
 * Arrow-key navigation between the items of `containerRef`: the arrow keys for `orientation` move
 * focus to the previous or next item and Home/End to the first or last, stopping at the ends unless
 * `wrap` is set. Items are matched in DOM order; hidden ones are skipped.
 *
 * By default the container is also a single Tab stop: the last focused item, else `tabStop` (a
 * selector, a predicate or an index into the items), else the first item. Pass `tabStop: null` to leave
 * `tabindex` alone, e.g. for items inside another roving container's item.
 *
 * Listens on the document, so it runs after React handlers inside the container and skips keys they
 * handled; keys from portaled menus and popovers never reach it. With `capture` it runs before them
 * instead, so its arrow keys win over items that use the same keys, like menu buttons that open on
 * ArrowDown/ArrowUp inside a vertical toolbar.
 *
 * @example
 * const toolbarRef = useRef<HTMLDivElement>(null);
 * useRovingNav(toolbarRef, "horizontal");
 * return <div ref={toolbarRef} role="toolbar" aria-orientation="horizontal" />;
 */
export function useRovingNav(
  containerRef: RefObject<HTMLElement | null>,
  orientation: keyof typeof KEYS,
  {
    itemSelector = "button:not(:disabled)",
    tabStop,
    homeEnd = true,
    capture = false,
    wrap = false,
    disabled = false,
  }: {
    itemSelector?: string;
    tabStop?: string | number | ((item: HTMLElement) => boolean) | null;
    /** `false` leaves Home/End to an outer roving container */
    homeEnd?: boolean;
    capture?: boolean;
    /** Moves from the last item to the first and back, as tabs do */
    wrap?: boolean;
    /** Takes every item out of the Tab order, e.g. inside a chat message that isn't entered */
    disabled?: boolean;
  } = {},
) {
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const itemsOf = (container: HTMLElement) =>
    Array.from(container.querySelectorAll<HTMLElement>(itemSelector)).filter(
      (item) => item.checkVisibility?.() ?? true,
    );

  const tabStopOf = (items: HTMLElement[]) => {
    if (typeof tabStop === "function") return items.find(tabStop);
    if (typeof tabStop === "string") return items.find((item) => item.matches(tabStop));
    return items[tabStop ?? 0];
  };

  const syncTabStop = (container: HTMLElement) => {
    if (tabStop === null) return;
    const items = itemsOf(container);
    const lastFocused = lastFocusedRef.current;
    const stop =
      (lastFocused && items.includes(lastFocused) ? lastFocused : undefined) ??
      tabStopOf(items) ??
      items[0];
    items.forEach((item) => {
      const tabIndex = item === stop && !disabled ? "0" : "-1";
      // Unchanged values are skipped so the mutation observer doesn't wake itself up
      if (item.getAttribute("tabindex") !== tabIndex) item.setAttribute("tabindex", tabIndex);
    });
  };

  // Nothing in the DOM changes when `disabled` does, so it re-syncs here
  useEffect(() => {
    if (containerRef.current) syncTabStop(containerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  useMutationObserver(containerRef, syncTabStop, {
    childList: true,
    subtree: true,
    attributes: true,
  });

  const onFocusIn = useEventCallback((event: FocusEvent) => {
    const container = containerRef.current;
    const target = event.target as Node;
    if (!container) return;
    const item = itemsOf(container).findLast((candidate) => candidate.contains(target));
    if (!item) return;
    lastFocusedRef.current = item;
    syncTabStop(container);
  });

  const onKeyDown = useEventCallback((event: KeyboardEvent) => {
    const container = containerRef.current;
    const target = event.target as HTMLElement;
    if (!container || event.defaultPrevented || !container.contains(target)) return;
    if (isTextField(target)) return;
    const items = itemsOf(container);
    const index = items.findLastIndex((item) => item.contains(target));
    if (index === -1) return;

    const keys = KEYS[orientation];
    let nextItem: HTMLElement | undefined;
    const at = (i: number) => (wrap ? items.at(i % items.length) : items[i]);
    if (event.key === keys.next) nextItem = at(index + 1);
    else if (event.key === keys.previous) nextItem = at(index - 1);
    else if (homeEnd && event.key === "Home") nextItem = items[0];
    else if (homeEnd && event.key === "End") nextItem = items.at(-1);
    else return;

    event.preventDefault();
    // Keeps the key from the item's own handlers, which would otherwise still see it
    if (capture) event.stopPropagation();
    nextItem?.focus();
  });

  useEffect(() => {
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("keydown", onKeyDown, { capture });
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKeyDown, { capture });
    };
  }, [onFocusIn, onKeyDown, capture]);
}
